import { Calendar, DollarSign, Hash, List, Type } from 'lucide-react';
import type * as React from 'react';

import { StatusChip } from '@/design';
import type {
  IAdminSecAdvFirmRow,
  ISecAdvCatalogue,
  ISecAdvSurveyStatus
} from '../../api/sec-adv-contracts';
import { formatDay } from '../../components/days';
import {
  CatalogueValue,
  FIRM_TYPE,
  codeText,
  formatCount,
  formatDollars
} from '../../components/sec-adv-values';
import { Dash, ERA_NOTE, ITEM_5G, attributeAt, optionLabel, yesNoOptions } from './register';

/**
 * The firm list's columns (SR-423): survey-api's typed list fields, then any attribute of the
 * catalogue, requested raw through `columns=`. A column taken from Form ADV names its item — shown
 * on hover as the SEC's monthly files head it — and each of its values links to that answer in the
 * firm's filing.
 */
type FieldKind = 'money' | 'number' | 'date' | 'text' | 'list';

/** A kind's icon in the Columns sheet: a dollar amount, a count, a day, one of a few values, text. */
export const KIND_ICON: Record<FieldKind, React.ComponentType<{ className?: string }>> = {
  money: DollarSign,
  number: Hash,
  date: Calendar,
  list: List,
  text: Type
};

export interface IListField {
  /** The list field's key, or a raw attribute's `<element path>.<attribute>`. */
  key: string;
  label: string;
  kind: FieldKind;
  /** Its Form ADV item, `5.F(2)(c)`. */
  item?: string;
  /** Where its value is in the record — the click-through's `?item=`. */
  source?: string;
  sortable: boolean;
  /** The picker's group. */
  group: string;
  /** Drawn in a cell; `null`/`undefined` is a dash. */
  render: (row: IAdminSecAdvFirmRow, labels: IFieldLabels) => React.ReactNode;
  /** Right-aligned. */
  numeric?: boolean;
  /** The cell's width, where its content would otherwise be clipped. */
  width?: string;
}

/** What a cell labels its codes with. */
interface IFieldLabels {
  catalogue: Nullable<ISecAdvCatalogue>;
}

/** The firm's name as it does business: always shown, first, frozen. */
export const NAME_FIELD = 'businessName';

const or = <T,>(value: Nullable<T> | undefined, show: (value: T) => React.ReactNode) =>
  value === null || value === undefined || (value as unknown) === '' ? null : show(value);

/** An amount, or a dash — with the reason for an exempt reporting adviser's. */
function money(row: IAdminSecAdvFirmRow, value: Nullable<number> | undefined): React.ReactNode {
  if (value !== null && value !== undefined) return formatDollars(value);
  return row.firmType === 'ERA' ? <Dash title={ERA_NOTE} /> : null;
}

const SURVEY_WORDS: Record<string, string> = {
  submitted: 'Submitted',
  in_progress: 'In progress',
  not_started: 'Not started'
};

export const SURVEY_OPTIONS = Object.entries(SURVEY_WORDS).map(([value, label]) => ({
  value,
  label
}));

function SurveyStatusChip({ status }: { status: ISecAdvSurveyStatus }) {
  const words = SURVEY_WORDS[status.status] ?? status.status;
  if (status.status === 'submitted') {
    return (
      <StatusChip tone="success" dot>
        {words}
        {status.submittedAt && ` ${formatDay(status.submittedAt.slice(0, 10))}`}
      </StatusChip>
    );
  }
  return (
    <StatusChip tone={status.status === 'in_progress' ? 'info' : 'neutral'} dot>
      {words}
    </StatusChip>
  );
}

/** Services: the first as a pill, the rest counted. */
function ServicesCell({
  codes,
  catalogue
}: {
  codes: string[];
  catalogue: Nullable<ISecAdvCatalogue>;
}) {
  if (codes.length === 0) return <Dash />;
  const options = yesNoOptions(catalogue, ITEM_5G);
  return (
    <span className="flex max-w-56 min-w-0 items-center gap-1.5 whitespace-nowrap">
      <StatusChip className="min-w-0 shrink">{optionLabel(options, codes[0])}</StatusChip>
      {codes.length > 1 && (
        <span className="shrink-0 text-xs text-muted-foreground">+{codes.length - 1} more</span>
      )}
    </span>
  );
}

export const FIRM_TYPE_OPTIONS = [
  { value: 'Registered', label: 'Registered' },
  { value: 'ERA', label: 'Exempt reporting adviser' }
];

/** survey-api's typed list fields, in the picker's order. */
export const LIST_FIELDS: IListField[] = [
  {
    key: 'businessName',
    label: 'Firm name',
    kind: 'text',
    item: '1.B(1)',
    sortable: true,
    group: 'Firm',
    render: (row) => row.businessName ?? row.legalName
  },
  {
    key: 'legalName',
    label: 'Legal name',
    kind: 'text',
    item: '1.A',
    sortable: true,
    group: 'Firm',
    render: (row) => row.legalName
  },
  {
    key: 'secNumber',
    label: 'SEC number',
    kind: 'text',
    item: '1.D',
    source: 'Info.SECNb',
    sortable: true,
    group: 'Firm',
    render: (row) => row.secNumber
  },
  {
    key: 'firmCrd',
    label: 'Firm CRD',
    kind: 'text',
    item: '1.E(1)',
    source: 'Info.FirmCrdNb',
    sortable: true,
    group: 'Firm',
    render: (row) => row.firmCrd
  },
  {
    key: 'firmType',
    label: 'Registration type',
    kind: 'list',
    sortable: true,
    group: 'Firm',
    render: (row, { catalogue }) =>
      optionLabel(FIRM_TYPE_OPTIONS, row.firmType) !== row.firmType
        ? optionLabel(FIRM_TYPE_OPTIONS, row.firmType)
        : codeText(catalogue, FIRM_TYPE, row.firmType)
  },
  {
    key: 'registrationStatus',
    label: 'Registration status',
    kind: 'list',
    sortable: true,
    group: 'Firm',
    render: (row, { catalogue }) =>
      or(row.registrationStatus, (status) => codeText(catalogue, { path: 'Rgstn', name: 'St' }, status))
  },
  {
    key: 'filingDate',
    label: 'Last reported',
    kind: 'date',
    sortable: true,
    group: 'Firm',
    render: (row) => or(row.filingDate, formatDay)
  },
  {
    key: 'surveyStatus',
    label: 'Survey status',
    kind: 'list',
    sortable: false,
    group: 'Firm',
    render: (row) => or(row.surveyStatus, (status) => <SurveyStatusChip status={status} />)
  },
  {
    key: 'participantCount',
    label: 'Participants',
    kind: 'number',
    sortable: false,
    numeric: true,
    group: 'Firm',
    render: (row) => row.participantCount
  },
  {
    key: 'totalAum',
    label: 'Total AUM',
    kind: 'money',
    item: '5.F(2)(c)',
    source: 'FormInfo.Part1A.Item5F.Q5F2C',
    sortable: true,
    numeric: true,
    group: 'Assets and clients',
    render: (row) => money(row, row.totalAum ?? row.raum)
  },
  {
    key: 'discretionaryAum',
    label: 'Discretionary AUM',
    kind: 'money',
    item: '5.F(2)(a)',
    source: 'FormInfo.Part1A.Item5F.Q5F2A',
    sortable: true,
    numeric: true,
    group: 'Assets and clients',
    render: (row) => money(row, row.discretionaryAum)
  },
  {
    key: 'nonDiscretionaryAum',
    label: 'Non-discretionary AUM',
    kind: 'money',
    item: '5.F(2)(b)',
    source: 'FormInfo.Part1A.Item5F.Q5F2B',
    sortable: true,
    numeric: true,
    group: 'Assets and clients',
    render: (row) => money(row, row.nonDiscretionaryAum)
  },
  {
    key: 'totalAccounts',
    label: 'Total accounts',
    kind: 'number',
    item: '5.F(2)(f)',
    source: 'FormInfo.Part1A.Item5F.Q5F2F',
    sortable: true,
    numeric: true,
    group: 'Assets and clients',
    render: (row) => or(row.totalAccounts, formatCount)
  },
  {
    key: 'clients',
    label: 'Clients',
    kind: 'number',
    item: '5.D',
    source: 'FormInfo.Part1A.Item5D',
    sortable: true,
    numeric: true,
    group: 'Assets and clients',
    render: (row) => or(row.clients, formatCount)
  },
  {
    key: 'employees',
    label: 'Employees',
    kind: 'number',
    item: '5.A',
    source: 'FormInfo.Part1A.Item5A.TtlEmp',
    sortable: true,
    numeric: true,
    group: 'Assets and clients',
    render: (row) => or(row.employees, formatCount)
  },
  {
    key: 'advisoryEmployees',
    label: 'Advisory employees',
    kind: 'number',
    item: '5.B(1)',
    source: 'FormInfo.Part1A.Item5B.Q5B1',
    sortable: true,
    numeric: true,
    group: 'Assets and clients',
    render: (row) => or(row.advisoryEmployees, formatCount)
  },
  {
    key: 'services',
    label: 'Services',
    kind: 'list',
    item: '5.G',
    source: ITEM_5G,
    sortable: false,
    group: 'Assets and clients',
    width: 'min-w-52',
    render: (row, { catalogue }) =>
      or(row.services, (codes) => <ServicesCell codes={codes} catalogue={catalogue} />)
  },
  {
    key: 'officeCity',
    label: 'Office city',
    kind: 'text',
    item: '1.F(1)',
    source: 'MainAddr.City',
    sortable: true,
    group: 'Office',
    render: (row) => or(row.officeCity ?? row.mainOffice?.city, (city) => city)
  },
  {
    key: 'officeState',
    label: 'Office region',
    kind: 'text',
    item: '1.F(1)',
    source: 'MainAddr.State',
    sortable: true,
    group: 'Office',
    render: (row) => or(row.officeState ?? row.mainOffice?.state, (state) => state)
  },
  {
    key: 'officeCountry',
    label: 'Office country',
    kind: 'text',
    item: '1.F(1)',
    source: 'MainAddr.Cntry',
    sortable: true,
    group: 'Office',
    render: (row) => or(row.officeCountry ?? row.mainOffice?.country, (country) => country)
  },
  {
    key: 'officePostalCode',
    label: 'Office ZIP code',
    kind: 'text',
    item: '1.F(1)',
    source: 'MainAddr.PostlCd',
    sortable: true,
    group: 'Office',
    render: (row) => or(row.officePostalCode, (code) => code)
  },
  {
    key: 'phone',
    label: 'Phone',
    kind: 'text',
    item: '1.F(3)',
    source: 'MainAddr.PhNb',
    sortable: true,
    group: 'Office',
    render: (row) => or(row.phone, (phone) => phone)
  },
  {
    key: 'website',
    label: 'Website',
    kind: 'text',
    item: '1.I',
    source: 'FormInfo.Part1A.Item1.WebAddrs',
    sortable: false,
    group: 'Office',
    render: (row) => or(row.website, (site) => site)
  }
];

/** What the list shows before anyone picks. */
export const DEFAULT_COLUMNS = [
  'businessName',
  'secNumber',
  'totalAum',
  'officeCity',
  'officeState',
  'services',
  'filingDate',
  'surveyStatus'
];

/** A raw attribute of the catalogue as a column: its label, its item, its value as the feed wrote
 *  it, read by the catalogue's type. */
export function rawField(catalogue: Nullable<ISecAdvCatalogue>, key: string): IListField {
  const found = attributeAt(catalogue, key);
  const attribute = found?.attribute ?? null;
  const kind: FieldKind =
    attribute?.valueType === 'amount_usd'
      ? 'money'
      : attribute?.valueType === 'integer'
        ? 'number'
        : attribute?.valueType === 'date' || attribute?.valueType === 'year_month'
          ? 'date'
          : attribute?.valueType === 'code' || attribute?.valueType === 'yes_no'
            ? 'list'
            : 'text';
  return {
    key,
    label: attribute?.label ?? key,
    kind,
    item: attribute?.item ?? undefined,
    source: key,
    sortable: false,
    numeric: kind === 'money' || kind === 'number',
    group: found ? groupOf(found.element.item, found.element.title) : 'Form ADV',
    render: (row) => {
      const value = row.values?.[key];
      if (value === null || value === undefined || value === '') return null;
      return <CatalogueValue attribute={attribute} value={value} />;
    }
  };
}

function groupOf(item: Nullable<string>, title: string): string {
  return item ? `Item ${item} · ${title}` : title;
}

/** Every catalogue attribute a column can show, grouped by its element — repeating elements
 *  (notice filings, websites) left out, since a row holds one value per column. */
export function rawFields(catalogue: Nullable<ISecAdvCatalogue>): IListField[] {
  const fields: IListField[] = [];
  for (const element of catalogue?.elements ?? []) {
    if (element.path.startsWith('NoticeFiled') || element.path.includes('WebAddr')) continue;
    for (const attribute of element.attributes) {
      const key = `${element.path}.${attribute.name}`;
      // An attribute a list field already reads is offered once, as that field.
      if (LIST_FIELDS.some((field) => field.source === key)) continue;
      fields.push(rawField(catalogue, key));
    }
  }
  return fields;
}
